// src/views/dashboard/salesrep/SalesRepDashboard.jsx
import React, { useState, useEffect } from "react";
import { 
  IoPersonOutline, 
  IoStatsChartOutline,
  IoDocumentTextOutline,
  IoTimeOutline,
  IoCartOutline,
  IoCheckmarkCircleOutline,
  IoPersonAddOutline,
  IoTrendingUpOutline,
  IoChevronForwardOutline,
  IoShieldCheckmarkOutline,
  IoFilter
} from "react-icons/io5";
import { TrendingUp, Activity, Users, FileText, Package, AlertCircle, Truck, Sparkles, Filter } from "lucide-react";
import ProvidersModal from "./ProvidersModal";
import IVRsModal from "./IVRsModal";
import BillingConfirmationModal from "./BillingConfirmationModal";

const SalesRepDashboard = ({ user }) => {
  const [stats, setStats] = useState({
    totalProviders: 0,
    activeProviders: 0,
    pendingProviders: 0,
    totalIvrs: 0,
    totalOrders: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [providers, setProviders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [displayedOrdersCount, setDisplayedOrdersCount] = useState(5);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedOrderForBilling, setSelectedOrderForBilling] = useState(null);
  // Modal states
  const [showProvidersModal, setShowProvidersModal] = useState(false);
  const [showIVRsModal, setShowIVRsModal] = useState(false);
  const [editingBillerEmail, setEditingBillerEmail] = useState(null); // order id
  const [billerEmailDraft, setBillerEmailDraft] = useState('');


  useEffect(() => {
    fetchDashboardData();
    fetchOrders();
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/dashboard-stats/', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      
      setStats({
        totalProviders: data.stats.total_providers || 0,
        activeProviders: data.stats.active_providers || 0,
        pendingProviders: data.stats.pending_providers || 0,
        totalIvrs: data.stats.total_ivrs || 0,
        totalOrders: data.stats.total_orders || 0,
      });
      setRecentActivities(data.recent_activities || []);
      setProviders(data.providers || []);
      
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setOrdersLoading(true);
      const url = statusFilter === 'all' 
        ? 'https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/orders/'
        : `https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/orders/?status=${statusFilter}`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      setOrders(data.data || []);
      setDisplayedOrdersCount(5); // Reset to show 5 when filter changes
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleCallStatusChange = async (orderId, newStatus) => {
    try {
      const currentOrder = orders.find(order => order.id === orderId);
      
      // ✅ Prevent un-selecting once it's been marked as complete
      if (currentOrder?.admin_call_complete && !newStatus) {
        alert('Cannot un-mark a completed call. This action is permanent.');
        return;
      }

      // ✅ If checking the box, show billing confirmation modal first
      if (newStatus) {
        setSelectedOrderForBilling(currentOrder);
        setShowBillingModal(true);
        return;
      }

      // If unchecking (which should not happen), proceed directly
      await updateCallStatus(orderId, newStatus, false);
      
    } catch (err) {
      console.error('Error updating call status:', err);
      alert('Failed to update call status');
    }
  };

  const handleBillingConfirmation = async (confirmed) => {
    setShowBillingModal(false);
    
    if (confirmed && selectedOrderForBilling) {
      await updateCallStatus(selectedOrderForBilling.id, true, true);
    }
    
    setSelectedOrderForBilling(null);
  };

  const handleBillerEmailUpdate = async (orderId) => {
    try {
      const response = await fetch(`https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/orders/${orderId}/biller-email/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ biller_email: billerEmailDraft }),
      });

      if (!response.ok) throw new Error('Failed to update biller email');

      setOrders(orders.map(order =>
        order.id === orderId ? { ...order, biller_email: billerEmailDraft } : order
      ));
    } catch (err) {
      console.error('Error updating biller email:', err);
      alert('Failed to update biller email');
    } finally {
      setEditingBillerEmail(null);
      setBillerEmailDraft('');
    }
  };


  const updateCallStatus = async (orderId, callComplete, billingConfirmed) => {
  try {
    const response = await fetch(`https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/orders/${orderId}/call-status/`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        admin_call_complete: callComplete,
        billing_documentation_confirmed: billingConfirmed
      })
    });

    if (!response.ok) {
      throw new Error('Failed to update call status');
    }

    // Update the local state
    setOrders(orders.map(order => 
      order.id === orderId 
        ? { 
            ...order, 
            admin_call_complete: callComplete,
            billing_documentation_confirmed: billingConfirmed
          }
        : order
    ));
  } catch (err) {
    console.error('Error updating call status:', err);
    alert('Failed to update call status');
  }
};


  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'completed':
      case 'added':
      case 'delivered':
      case 'granted':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'denied':
      case 'cancelled':
      case 'revoked':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'pending':
      case 'processing':
        return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      case 'placed':
      case 'shipped':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusGradient = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-gradient-to-r from-green-400 to-emerald-500 text-white shadow-lg';
      case 'cancelled':
      case 'refunded':
      case 'failed':
        return 'bg-gradient-to-r from-red-400 to-rose-500 text-white shadow-lg';
      case 'pending':
        return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-white shadow-lg';
      case 'processing':
        return 'bg-gradient-to-r from-blue-400 to-cyan-500 text-white shadow-lg';
      case 'shipped':
        return 'bg-gradient-to-r from-purple-400 to-pink-500 text-white shadow-lg';
      default:
        return 'bg-gradient-to-r from-gray-400 to-slate-500 text-white shadow-lg';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return '✓';
      case 'cancelled':
      case 'refunded':
      case 'failed':
        return '✕';
      case 'pending':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'shipped':
        return '🚚';
      default:
        return '📦';
    }
  };

  const getActivityIcon = (iconType) => {
    switch (iconType) {
      case 'document':
        return <IoDocumentTextOutline className="text-xl" />;
      case 'cart':
        return <IoCartOutline className="text-xl" />;
      case 'person':
        return <IoPersonAddOutline className="text-xl" />;
      case 'checkmark':
        return <IoCheckmarkCircleOutline className="text-xl" />;
      case 'shield':
        return <IoShieldCheckmarkOutline className="text-xl" />;
      default:
        return <IoDocumentTextOutline className="text-xl" />;
    }
  };

  const getActivityBgColor = (iconType) => {
    switch (iconType) {
      case 'document':
        return 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400';
      case 'cart':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
      case 'person':
        return 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400';
      case 'checkmark':
        return 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400';
      case 'shield':
        return 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const loadMoreOrders = () => {
    setDisplayedOrdersCount(prev => Math.min(prev + 5, orders.length));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-gray-200 dark:border-gray-700 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-teal-600 rounded-full animate-spin"></div>
        </div>
        <p className="mt-4 text-sm font-medium text-gray-500 dark:text-gray-400">
          Loading dashboard...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-12 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-red-500 mt-1 flex-shrink-0" size={24} />
            <div>
              <p className="font-semibold text-red-800 dark:text-red-300 mb-1">Error Loading Dashboard</p>
              <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button 
                onClick={fetchDashboardData}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-8 -mt-8">
      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Welcome back, {user?.full_name || 'Sales Representative'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Here's an overview of your provider network and activities
        </p>
      </div>

      {/* Stats Grid - 3 cards instead of 4 */}
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 mb-8">
        {/* Total Providers */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-lg flex items-center justify-center">
              <Users className="text-slate-600 dark:text-slate-400" size={24} />
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.totalProviders}
            </span>
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Total Providers
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            Assigned to you
          </p>
        </div>

        {/* Active Providers */}
        <button
          onClick={() => setShowProvidersModal(true)}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-teal-300 dark:hover:border-teal-700 transition-all text-left group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <IoCheckmarkCircleOutline className="text-teal-600 dark:text-teal-400 text-2xl" />
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.activeProviders}
            </span>
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Active Providers
          </p>
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-teal-600 dark:text-teal-400">
              Click to view details
            </p>
            <IoChevronForwardOutline className="text-gray-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" />
          </div>
        </button>

        {/* Total IVRs */}
        <button
          onClick={() => setShowIVRsModal(true)}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700 transition-all text-left group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
              <FileText className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.totalIvrs}
            </span>
          </div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            IVR Forms
          </p>
          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-blue-600 dark:text-blue-400">
              View all forms
            </p>
            <IoChevronForwardOutline className="text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
          </div>
        </button>
      </div>

      {/* Pending Access Alert */}
      {stats.pendingProviders > 0 && (
        <div className="mb-8 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertCircle className="text-amber-600 dark:text-amber-400" size={20} />
            </div>
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
                {stats.pendingProviders} {stats.pendingProviders === 1 ? 'Provider' : 'Providers'} Pending Access
              </p>
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Contact your assigned providers to request dashboard access for complete insights.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Orders Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Package className="text-purple-600 dark:text-purple-400" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Orders ({stats.totalOrders})
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Track and manage all orders
              </p>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-semibold">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
                <Filter className="text-white" size={16} />
              </div>
              <span className="text-sm">Filter:</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'all', label: 'All', gradient: 'from-gray-500 to-slate-600' },
                { value: 'pending', label: 'Pending', gradient: 'from-yellow-500 to-amber-600' },
                { value: 'processing', label: 'Processing', gradient: 'from-blue-500 to-cyan-600' },
                { value: 'shipped', label: 'Shipped', gradient: 'from-purple-500 to-pink-600' },
                { value: 'delivered', label: 'Delivered', gradient: 'from-green-500 to-emerald-600' },
                { value: 'cancelled', label: 'Cancelled', gradient: 'from-red-500 to-rose-600' }
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setStatusFilter(status.value)}
                  className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all duration-300 transform hover:scale-105 ${
                    statusFilter === status.value
                      ? `bg-gradient-to-r ${status.gradient} text-white scale-105 shadow-md`
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:shadow-md'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Orders Table */}
        {ordersLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-purple-200 dark:border-purple-800 rounded-full animate-pulse"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-purple-600 rounded-full animate-spin"></div>
            </div>
            <p className="mt-4 text-sm font-semibold text-gray-600 dark:text-gray-400 animate-pulse">
              Loading orders...
            </p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <IoCartOutline className="text-4xl text-gray-400" />
            </div>
            <p className="text-lg font-bold text-gray-500 dark:text-gray-400 mb-2">
              {statusFilter === 'all' 
                ? 'No orders placed yet' 
                : `No ${statusFilter} orders found`}
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">Orders will appear here when placed</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-800">
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Order #</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Patient</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Provider</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Facility</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Created</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Tracking</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Biller Email</th>
                    <th className="px-4 py-3 text-left text-xs font-black text-gray-700 dark:text-gray-300 uppercase">Call Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800">
                  {orders.slice(0, displayedOrdersCount).map((order, index) => (
                    <tr 
                      key={order.id}
                      className="group border-b border-gray-200 dark:border-gray-700 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 dark:hover:from-purple-900/20 dark:hover:to-pink-900/20 transition-all duration-300"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
                            <IoCartOutline className="text-white text-lg" />
                          </div>
                          <div>
                            <div className="text-sm font-bold text-gray-900 dark:text-white">
                              {order.order_number}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              ID: {order.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-bold text-gray-900 dark:text-white">
                          {order.patient_name}
                        </div>
                        {order.patient?.id && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            ID: {order.patient.id}
                          </div>
                        )}
                        {order.order_type === 'FAST_ORDER' && (
                          <div className="text-xs text-orange-500 dark:text-orange-400 font-bold flex items-center gap-1 mt-1">
                            🚀 Fast Order
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-bold text-gray-900 dark:text-white">
                          {order.provider_name}
                        </div>
                        {order.provider?.email && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {order.provider.email}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 max-w-xs truncate">
                          {order.facility_name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-black rounded-lg ${getStatusGradient(order.status)}`}>
                          <span>{getStatusIcon(order.status)}</span>
                          {order.status}
                        </span>
                        {order.status === 'delivered' && order.delivered_date && (
                          <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                            📅 {order.delivered_date}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {order.created_date}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {order.tracking_number ? (
                          <div className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-900 dark:to-cyan-900 rounded-lg">
                            <Truck className="text-blue-600 dark:text-blue-400" size={14} />
                            <span className="text-xs font-bold text-blue-700 dark:text-blue-300">
                              {order.tracking_number}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                        <td className="px-4 py-4">
                      {order.order_type === 'FAST_ORDER' ? (
                        editingBillerEmail === order.id ? (
                          <input
                            type="email"
                            value={billerEmailDraft}
                            onChange={(e) => setBillerEmailDraft(e.target.value)}
                            onBlur={() => handleBillerEmailUpdate(order.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleBillerEmailUpdate(order.id);
                              if (e.key === 'Escape') { setEditingBillerEmail(null); setBillerEmailDraft(''); }
                            }}
                            autoFocus
                            className="w-full px-2 py-1 text-xs border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                            placeholder="biller@example.com"
                          />
                        ) : (
                          <button
                            onClick={() => {
                              setEditingBillerEmail(order.id);
                              setBillerEmailDraft(order.biller_email || '');
                            }}
                            className="text-xs text-left w-full group"
                          >
                            {order.biller_email
                              ? <span className="text-gray-700 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{order.biller_email}</span>
                              : <span className="text-gray-400 italic group-hover:text-blue-500 transition-colors">+ Add email</span>
                            }
                          </button>
                        )
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                      <td className="px-4 py-4">
                        {order.order_type === 'FAST_ORDER' ? (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={order.admin_call_complete || false}
                              onChange={(e) => handleCallStatusChange(order.id, e.target.checked)}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                            />
                            <span className={`text-xs font-bold ${
                              order.admin_call_complete 
                                ? 'text-green-600 dark:text-green-400' 
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {order.admin_call_complete ? '✓ Done' : '⏳ Pending'}
                            </span>
                          </label>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Load More Button */}
            {displayedOrdersCount < orders.length && (
              <div className="mt-6 text-center">
                <button
                  onClick={loadMoreOrders}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl font-bold transition-all transform hover:scale-105 shadow-lg"
                >
                  Load More ({orders.length - displayedOrdersCount} remaining)
                </button>
              </div>
            )}

            {/* Showing count */}
            <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
              Showing {Math.min(displayedOrdersCount, orders.length)} of {orders.length} orders
            </div>
          </>
        )}
      </div>

      {/* Recent Activities */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
              <Activity className="text-teal-600 dark:text-teal-400" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Recent Activities
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Latest updates from your network
              </p>
            </div>
          </div>
        </div>
        
        {recentActivities.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <IoTimeOutline className="text-3xl text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
              No recent activities
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Activities from your providers will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentActivities.slice(0, 10).map((activity) => (
              <div 
                key={activity.id}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-10 h-10 ${getActivityBgColor(activity.icon)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    {getActivityIcon(activity.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                      {activity.name}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      {activity.type} • {activity.detail}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(activity.status)}`}>
                    {activity.status}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block whitespace-nowrap">
                    {activity.date_formatted}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showProvidersModal && (
        <ProvidersModal 
          isOpen={showProvidersModal}
          onClose={() => setShowProvidersModal(false)}
        />
      )}
      
      {showIVRsModal && (
        <IVRsModal 
          isOpen={showIVRsModal}
          onClose={() => setShowIVRsModal(false)}
        />
      )}

      {showBillingModal && (
  <BillingConfirmationModal
    isOpen={showBillingModal}
    onConfirm={() => handleBillingConfirmation(true)}
    onCancel={() => handleBillingConfirmation(false)}
    orderNumber={selectedOrderForBilling?.order_number}
  />
)}
    </div>
  );
};

export default SalesRepDashboard;